import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './AreaDetails.css';
import heroBackground from '../../media/hero-background.webp';
import contactImage from '../../media/contact.jpg';
import hookImage from '../../media/hook.png';
import batteryImage from '../../media/battery.png';
import fuelImage from '../../media/fuel.png';
import tiresImage from '../../media/tires.png';
import toolsImage from '../../media/tools.png';
import towedCarImage from '../../media/towed-car.png';
import whatWeOffer from '../../media/what-we-offer.png';
import article1 from '../../media/articles/1.jpg';
import article2 from '../../media/articles/2.jpg';
import article3 from '../../media/articles/3.jpg';
import article4 from '../../media/articles/4.jpg';
import article5 from '../../media/articles/5.jpg';
import article6 from '../../media/articles/6.jpg';
import { areas, regionsByGovernorate } from '../../data/areas';
import { areaCustomContent } from '../../data/areaCustomContent';
import { targetedAreas } from '../../data/targetedAreas';
import { areasConfig } from '../../data/areasConfig';
import { Car, Wrench, MapPin, Phone, MessageCircle, PhoneCall, Clock, CheckCircle, AlertTriangle, HelpCircle, ChevronDown, ChevronUp, Star, Shield, Award } from 'lucide-react';
import { phoneNumbers } from '../../data/phoneNumbers';
import { normalizeAreaName as normalize, toAreaSlug } from '../../utils/normalize';

const getAreaCustomData = (name) => {
    if (!name) return null;
    if (areaCustomContent[name]) return areaCustomContent[name];

    const normalizedTarget = normalize(name);
    for (const key of Object.keys(areaCustomContent)) {
        if (normalize(key) === normalizedTarget) {
            return areaCustomContent[key];
        }
    }
    return null;
};

const areaAliases = {
    'اميجو العين السخنه': 'طريق السخنه',
    'اميجو': 'طريق السخنه',
    'قريه اميجو': 'طريق السخنه',
    'قريه اميجو العين السخنه': 'طريق السخنه',
    'العين السخنه': 'طريق السخنه',
    'طريق العين السخنه': 'طريق السخنه',
    'طريق المحاجر العين السخنه': 'طريق السخنه',
    'طريق الجلاله الجديد': 'طريق السخنه',
    'طريق الجلاله': 'طريق السخنه',
    'الشركه الصينيه': 'طريق السخنه',
    'الزعفرانه': 'طريق السخنه',
    'اولاد صقر': 'كفر صقر',
    'مركز اولاد صقر': 'كفر صقر',
    'فوكا': 'مرسى مطروح',
    'الرديسيه': 'إدفو',
    'سيدي سالم': 'كفر الشيخ',
    'شربين': 'المنصورة',
    'نبروه': 'طلخا',
    'ميناء سفاجا': 'سفاجا',
    'ميناء السويس': 'السويس',
    'السويس الجديده': 'السويس',
    'مدينه العلمين الجديده': 'العلمين',
    'العلمين الجديده': 'العلمين',
    'النوباريه الجديده': 'دمنهور',
    'المنيا الجديده': 'المنيا',
    'ام قمر طريق السويس': 'طريق السويس'
};

const simplifyVariants = (s) => normalize(s)
    .replace(/ مدينه /g, ' ')
    .replace(/ المعادي المعادي/g, ' المعادي')
    .replace(/ الجيزه الجيزه/g, ' الجيزه')
    .replace(/ مصر الجديده/g, '')
    .replace(/ شبرا الخيمه/g, ' شبرا')
    .replace(/ الجيزه/g, '')
    .replace(/ المعادي/g, '')
    .replace(/ نصر/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const AreaDetails = () => {
    const { areaName } = useParams();

    const [activeFaq, setActiveFaq] = useState(null);

    const toggleFaq = (index) => {
        setActiveFaq(activeFaq === index ? null : index);
    };

    // Decode the URL parameter
    const decodedAreaName = decodeURIComponent(areaName);
    
    // Normalize input to find correct spelling in our lists
    let normalizedInput = normalize(decodedAreaName);
    if (areaAliases[normalizedInput]) {
        normalizedInput = normalize(areaAliases[normalizedInput]);
    }

    let matchedArea = areas.find(a => normalize(a) === normalizedInput) || 
                      targetedAreas.find(a => normalize(a) === normalizedInput);

    if (!matchedArea) {
        for (const g of regionsByGovernorate) {
            if (normalize(g.name) === normalizedInput) {
                matchedArea = g.name;
                break;
            }
            const foundSub = g.areas.find(a => normalize(a) === normalizedInput);
            if (foundSub) {
                matchedArea = foundSub;
                break;
            }
        }
    }

    // Flexible suffix / prefix variant matching (e.g. "شارع 7 المعادي" <=> "شارع ٧ مدينة المعادي")
    if (!matchedArea) {
        const simplified = simplifyVariants(decodedAreaName);
        matchedArea = areas.find(a => simplifyVariants(a) === simplified) ||
                      targetedAreas.find(a => simplifyVariants(a) === simplified);

        if (!matchedArea) {
            for (const g of regionsByGovernorate) {
                if (simplifyVariants(g.name) === simplified) {
                    matchedArea = g.name;
                    break;
                }
                const foundSub = g.areas.find(a => simplifyVariants(a) === simplified);
                if (foundSub) {
                    matchedArea = foundSub;
                    break;
                }
            }
        }
    }

    const areaExists = Boolean(matchedArea);
    const areaDisplayName = matchedArea || decodedAreaName.replace(/-/g, ' ');
    const effectiveNormalized = matchedArea ? normalize(matchedArea) : normalizedInput;

    // Find nearby areas (just get 12 random areas if we don't have a specific governorate)
    let nearbyAreas = [];
    let governorateName = "مصر";
    
    // Try to find which governorate this belongs to
    const governorate = regionsByGovernorate.find(g => normalize(g.name) === effectiveNormalized || g.areas.some(a => normalize(a) === effectiveNormalized));
    if (governorate) {
        governorateName = governorate.name;
        nearbyAreas = governorate.areas.filter(a => normalize(a) !== effectiveNormalized).slice(0, 11);
    } else {
        // Fallback
        nearbyAreas = areas.filter(a => normalize(a) !== effectiveNormalized).slice(0, 11);
    }

    // If area not found, render a 404
    if (!areaExists) {
        return (
            <div className="area-details-page">
                <Helmet>
                    <title>المنطقة غير موجودة | ونش انقاذ</title>
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <div style={{ padding: '100px 20px', textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    <h1 style={{ fontSize: '3rem', color: 'var(--accent)', marginBottom: '20px' }}>404</h1>
                    <h2>المنطقة غير موجودة</h2>
                    <p style={{ margin: '20px 0', fontSize: '1.1rem' }}>عذراً، لم نتمكن من العثور على المنطقة التي تبحث عنها.</p>
                    <Link to="/areas" className="cta-button primary" style={{ display: 'inline-block', marginTop: '10px' }}>العودة لمناطق الخدمة</Link>
                </div>
            </div>
        );
    }

    // Generate content based on area
    const generateContent = () => {
        const custom = getAreaCustomData(areaDisplayName);
        const configData = areasConfig.find(a => normalize(a.slug) === normalize(areaDisplayName) || normalize(a.name_ar) === normalize(areaDisplayName));
        const localContext = configData ? configData.local_context : '';
        const coverageNote = configData ? configData.coverage_note : '';
        const configLandmarks = configData ? configData.landmarks : [];
        const configAdjacent = configData ? configData.adjacent_areas : [];

        const defaultSafetyTips = [
            `تأكد من ركن السيارة على جانب الطريق بعيداً عن حركة المرور قدر الإمكان في ${areaDisplayName}.`,
            `قم بتشغيل أضواء الانتظار (الفلشر) فوراً لتنبيه السائقين الآخرين في ${areaDisplayName}.`,
            `إذا كنت على طريق سريع في ${areaDisplayName}، ابق داخل السيارة وأغلق الأبواب، أو قف خلف الحاجز الخرساني.`,
            `لا تقبل مساعدة من سيارات غير معروفة وتأكد من هوية سائق الونش عند وصوله في ${areaDisplayName}.`,
            `اتصل بنا فوراً على: ` + phoneNumbers.join(' أو ') + ` وسنرسل إليك الونش الأقرب في ${areaDisplayName}.`,
            `ضع مثلثات تحذير خلف سيارتك إذا توفرت معك في ${areaDisplayName}، خاصةً على الطرق السريعة.`,
            `لا تحاول دفع سيارتك بنفسك إذا تعطلت في منتصف طريق سريع في ${areaDisplayName} — أترك ذلك لفريقنا المحترف.`
        ];

        const defaultFaqs = [
            {
                q: `كم يستغرق وصول الونش في ${areaDisplayName}؟`,
                a: `نظراً لانتشار ونشاتنا في ${areaDisplayName}، نصل إليك عادةً خلال 15 إلى 20 دقيقة حسب حركة المرور. اتصل على ${phoneNumbers[0]} وسنعطيك وقت وصول دقيق.`
            },
            {
                q: `هل الخدمة متاحة طوال اليوم في ${areaDisplayName}؟`,
                a: `نعم، خدمات ونش إنقاذ ${areaDisplayName} متاحة 24 ساعة يومياً، 7 أيام في الأسبوع، بما في ذلك العطلات الرسمية والأعياد. فريقنا لا يتوقف أبداً!`
            },
            {
                q: `كم تكلفة خدمة الونش في ${areaDisplayName}؟`,
                a: `نقدم خصم 50% على جميع خدمات الونش في ${areaDisplayName}! التكلفة تعتمد على المسافة ونوع السيارة، لكننا نضمن أسعاراً تنافسية وشفافة بدون رسوم خفية. اتصل على ${phoneNumbers[0]} للحصول على عرض سعر فوري.`
            },
            {
                q: `ما أرقام التواصل مع ونش إنقاذ ${areaDisplayName}؟`,
                a: `يمكنك الاتصال بنا على ${phoneNumbers[0]} أو ${phoneNumbers[1]} أو ${phoneNumbers[2]}. نرد على مدار الساعة 24/7 في ${areaDisplayName} وجميع المناطق المحيطة.`
            },
            {
                q: `هل يوجد خصم على خدمة الونش في ${areaDisplayName}؟`,
                a: `نعم! نقدم خصم 50% حصري على جميع خدمات ونش إنقاذ ${areaDisplayName}. اتصل الآن على ${phoneNumbers[0]} واستفد من هذا العرض الرائع.`
            },
            {
                q: `هل تقدمون فاتورة ضريبية؟`,
                a: `نعم، نحن شركة مرخصة ويمكننا تقديم فواتير رسمية للشركات والأفراد عند الطلب.`
            },
            {
                q: `هل تنقلون السيارات الفارهة والكلاسيكية من ${areaDisplayName}؟`,
                a: `نعم، لدينا ونشات مسطحة (Flatbed) متخصصة لنقل السيارات الفارهة والكلاسيكية بعناية فائقة وأغطية واقية من ${areaDisplayName} إلى أي مكان.`
            },
            {
                q: `هل تعملون خارج ${areaDisplayName} للمحافظات الأخرى؟`,
                a: `نعم، نقدم خدمة نقل السيارات من ${areaDisplayName} إلى جميع محافظات مصر بأسعار تنافسية وونشات مخصصة للرحلات الطويلة.`
            }
        ];

        return {
            title: custom?.title || `ونش إنقاذ في ${areaDisplayName} | Winch Enqaz`,
            metaDescription: custom?.metaDescription || `ونش إنقاذ ${areaDisplayName} | خصم 50% على جميع الخدمات | اتصل ${phoneNumbers[0]} | خدمة 24 ساعة | وصول خلال 15 دقيقة | أمان تام لسيارتك`,
            intro: custom?.intro || `هل تعطلت سيارتك في ${areaDisplayName}؟ لا تقلق، نحن هنا لمساعدتك فوراً! ${localContext} ${coverageNote} نمتلك أحدث الونشات الهيدروليكية المجهزة لنقل جميع أنواع السيارات بأمان تام. اتصل بنا الآن على ${phoneNumbers[0]} واحصل على أسرع خدمة ونش إنقاذ في ${areaDisplayName}!`,
            configLandmarks: configLandmarks,
            configAdjacent: configAdjacent,
            landmarks: custom?.landmarks || [],
            customSeoText: custom?.seoText || null,
            safetyTips: custom?.safetyTips ? [...custom.safetyTips, ...defaultSafetyTips] : defaultSafetyTips,
            faqs: custom?.faqs ? [...custom.faqs, ...defaultFaqs] : defaultFaqs,
            whyUs: [
                {
                    icon: <Clock size={50} color='var(--accent)' />,
                    title: 'سرعة استجابة لا تُنافس',
                    desc: `لأننا نتمركز داخل ${areaDisplayName} ونستخدم نظام GPS متطور لتتبع حركة المرور، نضمن وصول الونش إليك في وقت قياسي أقل من 20 دقيقة. فريقنا يعمل على مدار الساعة بلا انقطاع.`
                },
                {
                    icon: <Car size={50} color='var(--accent)' />,
                    title: 'أمان تام لسيارتك',
                    desc: `نستخدم ونشات هيدروليكية حديثة ومعدات تثبيت خاصة وأحزمة معتمدة لضمان عدم تعرض سيارتك لأي خدش أو ضرر أثناء النقل. كل سيارة مؤمّنة بالكامل خلال النقل.`
                },
                {
                    icon: <Wrench size={50} color='var(--accent)' />,
                    title: 'خبرة فنية واسعة',
                    desc: `سائقونا ليسوا مجرد سائقين، بل فنيين مدربين ومعتمدين بخبرة تزيد عن 15 عاماً في مجال إنقاذ السيارات في ${areaDisplayName} وجميع محافظات مصر.`
                },
                {
                    icon: <Shield size={50} color='var(--accent)' />,
                    title: 'تأمين شامل',
                    desc: `جميع السيارات المنقولة مؤمّنة بالكامل ضد أي أضرار أثناء عملية النقل من شركات تأمين معتمدة، لراحة بالك التامة في ${areaDisplayName}.`
                },
                {
                    icon: <Award size={50} color='var(--accent)' />,
                    title: 'أكثر من 15 عاماً خبرة',
                    desc: `خبرة حقيقية مع آلاف عمليات الإنقاذ الناجحة في ${areaDisplayName} وسائر محافظات مصر. تقييم 4.9 نجمة من أكثر من 1250 عميل.`
                }
            ],
            services: [
                {
                    title: 'سحب ونقل السيارات',
                    desc: `خدمة سحب آمنة لجميع أنواع السيارات (ملاكي، دفع رباعي، نقل) من أي مكان في ${areaDisplayName} بخصم 50%.`,
                    img: towedCarImage,
                    alt: `سحب ونقل السيارات في ${areaDisplayName}`
                },
                {
                    title: 'إنقاذ حوادث الطرق',
                    desc: `دعم طوارئ متخصص للتعامل مع حوادث الطرق ورفع السيارات المتضررة بعناية فائقة في ${areaDisplayName}.`,
                    img: hookImage,
                    alt: `إنقاذ حوادث الطرق ${areaDisplayName}`
                },
                {
                    title: 'شحن البطاريات وتغيير الإطارات',
                    desc: `شحن البطارية الفارغة، تغيير الإطارات المثقوبة، وتوصيل الوقود أينما كنت في ${areaDisplayName} بخصم 50%.`,
                    img: batteryImage,
                    alt: `شحن بطارية السيارة ${areaDisplayName}`
                },
                {
                    title: 'توصيل الوقود',
                    desc: `نسيت تعبئة الوقود؟ نوصل الوقود إلى موقعك مباشرةً في ${areaDisplayName} (80 - 92 - 95 - ديزل) دون انتظار.`,
                    img: fuelImage,
                    alt: `توصيل وقود طوارئ ${areaDisplayName}`
                },
                {
                    title: 'تغيير الإطارات الميكانيكية',
                    desc: `خدمة تغيير الإطارات في الموقع بأدوات احترافية، حتى لا تضطر لانتظار ورشة بعيدة في ${areaDisplayName}.`,
                    img: tiresImage,
                    alt: `تغيير إطارات ${areaDisplayName}`
                },
                {
                    title: 'إصلاح أعطال بسيطة',
                    desc: `فريقنا يحمل معدات أساسية يمكنهم من حل الأعطال البسيطة في مكانك دون الحاجة لسحب السيارة في ${areaDisplayName}.`,
                    img: toolsImage,
                    alt: `صيانة وإصلاح طوارئ ${areaDisplayName}`
                },
                {
                    title: 'نقل المعدات والآلات',
                    desc: `نمتلك ونشات مجهزة لنقل المعدات الخفيفة والمتوسطة، الكرفانات، والدرجات النارية من وإلى ${areaDisplayName}.`,
                    img: article3,
                    alt: `نقل معدات وآلات ${areaDisplayName}`
                },
                {
                    title: 'انتشال السيارات العالقة',
                    desc: `لدينا الخبرة والمعدات لانتشال السيارات العالقة في الرمال أو الأماكن الوعرة في ${areaDisplayName}.`,
                    img: article5,
                    alt: `انتشال سيارات عالقة ${areaDisplayName}`
                },
                {
                    title: 'نقل للمحافظات',
                    desc: `خدمة نقل السيارات من ${areaDisplayName} إلى أي محافظة أخرى في مصر بأسعار تنافسية وخصم 50%.`,
                    img: article2,
                    alt: `نقل سيارات بين المحافظات من ${areaDisplayName}`
                }
            ],
            richServices: [
                {
                    title: `سحب سيارات في ${areaDisplayName} — جميع الأنواع`,
                    body: `خدمة <strong>سحب سيارات ${areaDisplayName}</strong> لدينا تشمل جميع أنواع المركبات: سيارات ملاكي (Toyota, Hyundai, Kia, Nissan, Skoda, Volkswagen, Mercedes, BMW, Audi)، سيارات دفع رباعي SUV (Jeep, Land Cruiser, Patrol, Fortuner, Pajero)، ميني باصات، شاحنات خفيفة، دراجات نارية، وكرفانات. لدينا ونشات مسطحة (Flatbed) متخصصة للسيارات الفارهة والكلاسيكية بأغطية واقية. كل عمليات السحب في ${areaDisplayName} تتم بأحزمة تثبيت معتمدة وسادات حماية للهيكل والإطارات. اتصل على <strong>${phoneNumbers[0]}</strong> واحصل على <strong>خصم 50%</strong> فوراً!`
                },
                {
                    title: `شحن بطارية سيارة في ${areaDisplayName}`,
                    body: `نفاد البطارية من أكثر مشاكل السيارات شيوعاً في ${areaDisplayName}. خدمة <strong>شحن بطارية ${areaDisplayName}</strong> لدينا تصلك خلال 20 دقيقة. نستخدم معدات شحن احترافية إجمالية ومعززات دفع تحافظ على النظام الكهربائي لسيارتك. إذا كانت البطارية تالفة تماماً، يمكننا توصيل بطارية جديدة وتركيبها فوراً في ${areaDisplayName}. اتصل على <strong>${phoneNumbers[0]}</strong> بخصم <strong>50%</strong>.`
                },
                {
                    title: `تغيير إطار سيارة في ${areaDisplayName}`,
                    body: `ثقب الإطار في أحد شوارع ${areaDisplayName}؟ فريقنا يصل إليك ويقوم بتغيير <strong>إطار سيارة ${areaDisplayName}</strong> في الموقع بأدوات متخصصة وبسرعة قياسية. نقوم بفحص ضغط الهواء في جميع الإطارات، وإذا كان الإطار لا يوجد له إطار احتياطي نقل السيارة إلى أقرب مركز خدمة في ${areaDisplayName}. اتصل على <strong>${phoneNumbers[0]}</strong> واحصل على خصم <strong>50%</strong>.`
                },
                {
                    title: `توصيل وقود في ${areaDisplayName} — جميع أنواع الوقود`,
                    body: `نسيت تعبئة الوقود ووقفت سيارتك في ${areaDisplayName}؟ نقدم <strong>توصيل وقود ${areaDisplayName}</strong> لجميع أنواع الوقود: بنزين 80، بنزين 92، بنزين 95، ديزل. نصل إليك خلال 20 دقيقة بمعدات نقل وقود آمنة ومرخصة حتى تتمكن من متابعة طريقك. اتصل على <strong>${phoneNumbers[0]}</strong> واحصل على خصم <strong>50%</strong>.`
                },
                {
                    title: `فتح سيارة مغلقة في ${areaDisplayName}`,
                    body: `نسيت مفتاح سيارتك بداخلها في ${areaDisplayName}؟ خدمة <strong>فتح سيارات ${areaDisplayName}</strong> المغلقة متاحة معنا 24 ساعة. نستخدم أدوات متخصصة لفتح سيارتك بأمان تام دون إثر أي خدش على السيارة. نعمل على جميع ماركات السيارات. اتصل على <strong>${phoneNumbers[0]}</strong> بخصم <strong>50%</strong>.`
                },
                {
                    title: `ونش نقل سيارات فارهة في ${areaDisplayName}`,
                    body: `تمتلك سيارة فارهة أو كلاسيكية في ${areaDisplayName} وتحتاج لنقلها؟ نقدم <strong>ونش Flatbed</strong> متخصص لكل أنواع السيارات الفارهة (Mercedes, BMW, Porsche, Ferrari, Lamborghini, Rolls Royce, Audi, Bentley) بأغطية واقية تحافظ على الطلاء والهيكل بأمان تام. اتصل على <strong>${phoneNumbers[0]}</strong> بخصم 50%.`
                }
            ],
            pricingTable: [
                { service: `سحب سيارة داخل ${areaDisplayName}`, before: 'متغير', after: 'خصم 50%', note: 'يشمل جميع السيارات' },
                { service: `سحب سيارة خارج ${areaDisplayName}`, before: 'متغير', after: 'خصم 50%', note: 'حسب المسافة' },
                { service: 'شحن بطارية', before: 'متغير', after: 'خصم 50%', note: 'جميع السيارات' },
                { service: 'تغيير إطار', before: 'متغير', after: 'خصم 50%', note: 'في الموقع' },
                { service: 'توصيل وقود', before: 'متغير', after: 'خصم 50%', note: '80، 92، 95، ديزل' },
                { service: 'فتح سيارة مغلقة', before: 'متغير', after: 'خصم 50%', note: 'جميع الماركات' },
                { service: 'نقل للمحافظات', before: 'متغير', after: 'خصم 50%', note: 'جميع محافظات مصر' },
            ],
            testimonials: [
                {
                    name: 'محمد عبد العزيز',
                    rating: 5,
                    text: `خدمة ممتازة! وصل الونش في ${areaDisplayName} خلال 15 دقيقة بالضبط. السائق كان محترف جداً والسيارة وصلت سالمة تماماً. أنصح بهم بشدة!`,
                    date: 'منذ يومين'
                },
                {
                    name: 'أحمد سامي',
                    rating: 5,
                    text: `تعطلت سيارتي في الساعة 2 صباحاً في ${areaDisplayName} ولم أجد أحداً يساعدني. اتصلت بهم ووصلوا خلال 20 دقيقة وحلوا المشكلة. شكراً جزيلاً!`,
                    date: 'منذ أسبوع'
                },
                {
                    name: 'سارة محمود',
                    rating: 5,
                    text: `اتصلت بهم لأول مرة في ${areaDisplayName} وكنت خائفة من تجربة سيئة. لكن السائق كان لطيفاً ومتعاوناً ووصل بسرعة. خدمة احترافية 100%.`,
                    date: 'منذ أسبوعين'
                },
                {
                    name: 'كريم عبدالله',
                    rating: 5,
                    text: `أفضل ونش جربته في ${areaDisplayName}. سعر مناسب، والخدمة سريعة، والسيارة وصلت بدون أي خدش. سأتصل بهم دائماً!`,
                    date: 'منذ شهر'
                }
            ],
            processSteps: [
                {
                    icon: <PhoneCall size={30} />,
                    title: 'اتصل بنا',
                    desc: `اتصل برقم الطوارئ ${phoneNumbers[0]} أو تواصل عبر واتساب وأخبرنا بموقعك ونوع السيارة.`
                },
                {
                    icon: <MapPin size={30} />,
                    title: 'شارك موقعك',
                    desc: `أرسل موقعك عبر GPS أو أخبرنا اسم الشارع في ${areaDisplayName} وسنتحرك إليك فوراً.`
                },
                {
                    icon: <Clock size={30} />,
                    title: 'انتظر بأمان',
                    desc: `سيصلك الونش خلال 15–20 دقيقة في ${areaDisplayName}. ابق داخل السيارة أو بعيداً عن حركة المرور.`
                },
                {
                    icon: <CheckCircle size={30} />,
                    title: 'إتمام الخدمة',
                    desc: `ننقل سيارتك بأمان لأي وجهة تختارها وتحصل على فاتورة واضحة وخصم 50%.`
                }
            ],

            coverage: `نخدم ${areaDisplayName} كجزء من تغطيتنا الشاملة لمحافظة ${governorateName}.`,
            nearby: nearbyAreas
        };
    };

    const content = generateContent();
    const displayName = areaDisplayName;
    const configData = areasConfig.find(a => normalize(a.slug) === normalize(displayName) || normalize(a.name_ar) === normalize(displayName));
    const areaSlug = configData ? configData.slug : toAreaSlug(displayName);
    const canonicalUrl = `https://winchenqaz.com/areas/${encodeURIComponent(areaSlug)}/`;

    // Structured Data (Schema.org) - AutomotiveBusiness
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "AutomotiveBusiness",
        "@id": `${canonicalUrl}#localbusiness`,
        "name": `ونش انقاذ ${displayName}`,
        "description": content.metaDescription,
        "url": canonicalUrl,
        "telephone": phoneNumbers.map(p => `+2${p}`),
        "areaServed": {
            "@type": "Place",
            "name": displayName
        },
        "parentOrganization": {
            "@id": "https://winchenqaz.com/#organization"
        },
        "priceRange": "$$",
        "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
                "Monday", "Tuesday", "Wednesday", "Thursday",
                "Friday", "Saturday", "Sunday"
            ],
            "opens": "00:00",
            "closes": "23:59"
        },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": 1250
        },
        "image": [heroBackground, contactImage, whatWeOffer],
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": `خدمات ونش انقاذ ${displayName}`,
            "itemListElement": content.services.map((service, i) => ({
                "@type": "Offer",
                "itemOffered": {
                    "@type": "Service",
                    "name": service.title,
                    "description": service.desc
                }
            }))
        }
    };

    // FAQ Schema deprecated and removed

    // BreadcrumbList Schema
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "الرئيسية",
                "item": "https://winchenqaz.com/"
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": "المناطق",
                "item": "https://winchenqaz.com/areas/"
            },
            {
                "@type": "ListItem",
                "position": 3,
                "name": governorateName,
                "item": `https://winchenqaz.com/areas/${encodeURIComponent(toAreaSlug(governorateName))}/`
            },
            {
                "@type": "ListItem",
                "position": 4,
                "name": displayName,
                "item": canonicalUrl
            }
        ]
    };

    let finalTitle = content.title;
    // SEO: Phone and discount moved to meta description

    return (
        <div className="area-details-page">
            <Helmet>
                <title>{finalTitle}</title>
                <meta name="description" content={content.metaDescription} />
                <meta name="keywords" content={`ونش انقاذ ${displayName}, ونش انقاذ ${displayName} خصم 50%, رقم ونش انقاذ ${displayName}, تليفون ونش انقاذ ${displayName}, ${phoneNumbers[0]}, اسرع ونش انقاذ في ${displayName}, سحب سيارة ${displayName}, ونش سيارة ${displayName}, خدمة طوارئ ${displayName}, شحن بطارية ${displayName}, تغيير إطار ${displayName}, توصيل وقود ${displayName}, ونش انقاذ 24 ساعة ${displayName}, ونش, انقاذ, سيارات, خصم 50%, ${displayName}`} />
                <meta property="og:title" content={`${content.title} | ونش انقاذ سريع | ${phoneNumbers[0]}`} />
                <meta property="og:description" content={content.metaDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content="https://winchenqaz.com/logo.png" />
                <link rel="canonical" href={canonicalUrl} />
                <link rel="preload" as="image" href={heroBackground} />
                {!targetedAreas.some(a => normalize(a) === normalizedInput) && (
                    <meta name="robots" content="noindex, nofollow" />
                )}
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
            </Helmet>

            {/* Hero Section */}
            <section className="area-details-hero" style={{ backgroundImage: `url(${heroBackground})` }}>
                <div className="area-details-hero-overlay"></div>
                <div className="area-details-hero-content">
                    <h1 className="area-details-hero-title">
                        ونش إنقاذ <span className="highlight">{displayName}</span>
                    </h1>
                    <div className="hero-discount-badge">
                        <span className="discount-text">🔥 خصم 50% على جميع الخدمات</span>
                        <a href={`tel:+2${phoneNumbers[0]}`} className="discount-phone">{phoneNumbers[0]}</a>
                    </div>
                    <div className="hero-rating">
                        <div className="stars">
                            <Star fill="#ffc107" color="#ffc107" size={20} />
                            <Star fill="#ffc107" color="#ffc107" size={20} />
                            <Star fill="#ffc107" color="#ffc107" size={20} />
                            <Star fill="#ffc107" color="#ffc107" size={20} />
                            <Star fill="#ffc107" color="#ffc107" size={20} />
                        </div>
                        <span className="rating-text">4.9 (1,250 تقييم)</span>
                    </div>
                    <p className="area-details-hero-subtitle">أسرع ونش انقاذ 24/7 في {displayName} | {phoneNumbers[0]}</p>

                    {/* Phone numbers bar in hero */}
                    <div className="hero-phones">
                        {phoneNumbers.map((num, i) => (
                            <a key={i} href={`tel:+2${num}`} className="hero-phone-link">
                                <Phone size={16} />
                                <span>{num}</span>
                            </a>
                        ))}
                    </div>

                    <div className="breadcrumb">
                        <Link to="/">الرئيسية</Link>
                        <span className="separator">/</span>
                        <Link to="/areas/">المناطق</Link>
                        <span className="separator">/</span>
                        {governorateName !== displayName && (
                            <>
                                <Link to={`/areas/${encodeURIComponent(toAreaSlug(governorateName))}/`}>{governorateName}</Link>
                                <span className="separator">/</span>
                            </>
                        )}
                        <span>{displayName}</span>
                    </div>
                </div>
            </section>

            {/* Introduction */}
            <section className="area-intro">
                <div className="intro-container">
                    <div className="intro-content">
                        <h2 className="intro-title">{content.title}</h2>
                        <p className="intro-text">{content.intro}</p>
                        {content.landmarks && content.landmarks.length > 0 && (
                            <div className="area-landmarks-container">
                                <h3 className="landmarks-title">📍 أبرز المحاور والشوارع المعلمة التي نغطيها في {displayName}:</h3>
                                <div className="landmarks-badges">
                                    {content.landmarks.map((landmark, idx) => (
                                        <span key={idx} className="landmark-badge">{landmark}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {/* Inline phone cards */}
                        <div className="intro-phone-cards">
                            {phoneNumbers.map((num, i) => (
                                <a key={i} href={`tel:+2${num}`} className="intro-phone-card">
                                    <Phone size={18} color="var(--accent)" />
                                    <span>{num}</span>
                                </a>
                            ))}
                        </div>
                    </div>

                </div>
            </section>

            {/* Why Choose Us */}
            <section className="area-why-us">
                <div className="why-us-container">
                    <div className="why-us-header">
                        <h3 className="section-subtitle">مميزات الخدمة</h3>
                        <h2 className="section-title">
                            لماذا نحن الأفضل في <span className="highlight">{displayName}</span>
                        </h2>
                        <div className="title-divider">
                            <span className="star">★</span>
                            <span className="star">★</span>
                            <span className="star">★</span>
                            <span className="star">★</span>
                        </div>
                    </div>
                    <div className="why-us-grid">
                        {content.whyUs.map((item, index) => (
                            <div key={index} className="why-us-card">
                                <div className="why-us-icon">{item.icon}</div>
                                <h3 className="why-us-title">{item.title}</h3>
                                <p className="why-us-desc">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Service Process */}
            <section className="service-process">
                <div className="process-container">
                    <h2 className="section-title center">كيف تطلب الونش؟</h2>
                    <p className="section-desc center">خطوات بسيطة تفصلك عن وصول المساعدة</p>
                    <div className="process-steps">
                        {content.processSteps.map((step, index) => (
                            <div key={index} className="process-step">
                                <div className="step-icon-wrapper">
                                    {step.icon}
                                    <span className="step-number">{index + 1}</span>
                                </div>
                                <h3 className="step-title">{step.title}</h3>
                                <p className="step-desc">{step.desc}</p>
                                {index < content.processSteps.length - 1 && <div className="step-connector"></div>}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Offered — with images */}
            <section className="area-services">
                <div className="services-container">
                    <div className="services-header">
                        <h3 className="section-subtitle">ما نقدمه</h3>
                        <h2 className="section-title">
                            خدماتنا في <span className="highlight">{displayName}</span>
                        </h2>
                        <div className="title-divider">
                            <span className="star">★</span>
                            <span className="star">★</span>
                            <span className="star">★</span>
                            <span className="star">★</span>
                        </div>
                    </div>
                    <div className="services-grid-images">
                        {content.services.map((service, index) => (
                            <div key={index} className="service-card-img">
                                <div className="service-card-img-wrap">
                                    <img src={service.img} alt={service.alt} loading="lazy" width={600} height={400} />
                                </div>
                                <div className="service-card-body">
                                    <div className="service-number">{String(index + 1).padStart(2, '0')}</div>
                                    <h3 className="service-title">{service.title}</h3>
                                    <p className="service-desc">{service.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Article Images Gallery */}
            <section className="area-gallery">
                <div className="gallery-container">
                    <h2 className="section-title center">من مشاريعنا في {displayName}</h2>
                    <p className="section-desc center">صور حقيقية من عمليات إنقاذ ونقل السيارات</p>
                    <div className="gallery-grid">
                        {[article1, article2, article3, article4, article5, article6].map((img, i) => (
                            <div key={i} className="gallery-item">
                                <img
                                    src={img}
                                    alt={`عملية إنقاذ ونش في ${displayName} - صورة ${i + 1}`}
                                    loading="lazy"
                                    width={600}
                                    height={400}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Safety Tips */}
            <section className="safety-section">
                <div className="safety-container">
                    <div className="safety-header">
                        <AlertTriangle className="safety-icon-large" size={40} />
                        <h2>نصائح لسلامتك أثناء الانتظار</h2>
                    </div>
                    <div className="safety-list">
                        {content.safetyTips.map((tip, index) => (
                            <div key={index} className="safety-item">
                                <CheckCircle className="check-icon" size={20} />
                                <p>{tip}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Coverage Info */}
            <section className="coverage-info">
                <div className="coverage-container">
                    <div className="coverage-content">
                        <div className="coverage-text">
                            <h2 className="coverage-title">تغطية شاملة في {displayName}</h2>
                            <p className="coverage-description">{content.coverage}</p>
                            <p className="coverage-description">
                                نحن نفخر بكوننا الشركة الأكثر موثوقية في {displayName}، مع سجل حافل بآلاف
                                عمليات الإنقاذ الناجحة. فريقنا يعرف المنطقة جيداً ويمتلك الخبرة اللازمة للتعامل
                                مع جميع أنواع الحالات الطارئة.
                            </p>
                            <ul className="coverage-features">
                                <li>✓ خدمة 24 ساعة طوال أيام الأسبوع</li>
                                <li>✓ أسرع وقت استجابة في المنطقة</li>
                                <li>✓ أسعار تنافسية وشفافة</li>
                                <li>✓ فريق محترف ومدرب</li>
                                <li>✓ معدات حديثة وآمنة</li>
                                <li>✓ تأمين شامل على السيارات</li>
                            </ul>
                            {/* Phone numbers within coverage */}
                            <div className="coverage-phones">
                                <p className="coverage-phones-label">اتصل بنا الآن:</p>
                                <div className="coverage-phones-list">
                                    {phoneNumbers.map((num, i) => (
                                        <a key={i} href={`tel:+2${num}`} className="coverage-phone-btn">
                                            <Phone size={16} />
                                            {num}
                                        </a>
                                    ))}
                                </div>
                            </div>
                            <Link to="/contact" className="coverage-cta">احجز الخدمة الآن</Link>
                        </div>
                        <div className="coverage-image">
                            <img src={article1} alt={`ونش إنقاذ سيارات في ${displayName}`} loading="lazy" width={600} height={400} />
                        </div>
                    </div>
                </div>
            </section>

            {/* Nearby Areas */}
            <section className="nearby-areas">
                <div className="nearby-container">
                    <h3 className="nearby-title">مناطق قريبة نخدمها أيضاً</h3>
                    <div className="nearby-grid">
                        {content.nearby.map((nearbyArea, index) => (
                            <Link
                                key={index}
                                to={`/areas/${encodeURIComponent(nearbyArea)}`}
                                className="nearby-card"
                            >
                                <span className="nearby-icon"><MapPin color='var(--accent)'
                                    style={
                                        {
                                            marginBottom: '-6px'
                                        }
                                    }
                                /></span>
                                <span className="nearby-name">ونش انقاذ {nearbyArea}</span>
                            </Link>
                        ))}
                    </div>
                    <Link to="/areas" className="view-all-areas">عرض جميع المناطق ←</Link>
                </div>
            </section>

            {/* Rich Services Detail Section */ }
    <section className="rich-services-section">
        <div className="rich-services-container">
            <div className="rich-services-header">
                <h2>خدمات ونش الإنقاذ التفصيلية في {displayName} | خصم 50% | {phoneNumbers[0]}</h2>
                <p>نقدم مجموعة شاملة من خدمات إنقاذ السيارات في {displayName}. كل خدمة بخصم 50% عند الاتصال على <strong>{phoneNumbers[0]}</strong></p>
            </div>
            <div className="rich-services-grid">
                {content.richServices.map((svc, i) => (
                    <div key={i} className="rich-service-card">
                        <h3>{svc.title}</h3>
                        <p dangerouslySetInnerHTML={{ __html: svc.body }} />
                        <a href={`tel:+2${phoneNumbers[0]}`} className="rich-service-cta">
                            📞 اتصل الآن — خصم 50%
                        </a>
                    </div>
                ))}
            </div>
        </div>
    </section>

    {/* Pricing Table Section */ }
    <section className="pricing-section">
        <div className="pricing-container">
            <div className="pricing-header">
                <h2>أسعار خدمات ونش الإنقاذ في {displayName} | خصم 50% على الكل</h2>
                <p>جميع الأسعار تشمل خصم 50% عند الاتصال على <strong>{phoneNumbers[0]}</strong></p>
            </div>
            <div className="pricing-table-wrapper">
                <table className="pricing-table">
                    <thead>
                        <tr>
                            <th>الخدمة</th>
                            <th>السعر العادي</th>
                            <th>سعرك مع الخصم</th>
                            <th>ملاحظة</th>
                        </tr>
                    </thead>
                    <tbody>
                        {content.pricingTable.map((row, i) => (
                            <tr key={i}>
                                <td><strong>{row.service}</strong></td>
                                <td><span className="price-before">{row.before}</span></td>
                                <td><span className="price-after">{row.after}</span></td>
                                <td>{row.note}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="pricing-cta">
                <p>للحصول على عرض سعر دقيق، اتصل الآن على <strong>{phoneNumbers[0]}</strong> أو <strong>{phoneNumbers[1]}</strong></p>
                <div className="pricing-buttons">
                    {phoneNumbers.map((num, i) => (
                        <a key={i} href={`tel:+2${num}`} className="pricing-call-btn">{num}</a>
                    ))}
                </div>
            </div>
        </div>
    </section>

    {/* Testimonials Section */ }
    <section className="area-testimonials-section">
        <div className="area-testimonials-container">
            <div className="area-testimonials-header">
                <h2>آراء عملاء ونش الإنقاذ في {displayName}</h2>
                <p>تقييم 4.9 ⭐ من أكثر من 1,250 عميل | اتصل على {phoneNumbers[0]}</p>
            </div>
            <div className="area-testimonials-grid">
                {content.testimonials.map((t, i) => (
                    <div key={i} className="area-testimonial-card">
                        <div className="testimonial-stars">
                            {'⭐'.repeat(t.rating)}
                        </div>
                        <p className="testimonial-text">"{t.text}"</p>
                        <div className="testimonial-author">
                            <strong>{t.name}</strong>
                            <span>{t.date}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>

    {/* FAQ Section */ }
    <section className="area-faq">
        <div className="area-faq-container">
            <h2 className="section-title center">أسئلة شائعة</h2>
            <div className="faq-grid">
                {content.faqs.map((faq, index) => (
                    <div
                        key={index}
                        className={`faq-card ${activeFaq === index ? 'active' : ''}`}
                        onClick={() => toggleFaq(index)}
                    >
                        <div className="area-faq-header">
                            <div className="faq-question">
                                <HelpCircle className="faq-icon" size={24} />
                                <h3>{faq.q}</h3>
                            </div>
                            <div className="faq-toggle">
                                {activeFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </div>
                        </div>
                        <div className={`faq-answer-wrapper ${activeFaq === index ? 'open' : ''}`}>
                            <p className="area-faq-answer">{faq.a}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>

    {/* SEO Content */ }
    <section className="seo-content">
        <div className="seo-container">
            <div className="seo-text">
                {content.customSeoText && (
                    <div className="custom-area-seo-article" dangerouslySetInnerHTML={{ __html: content.customSeoText }} />
                )}
                <h2>لماذا نحن الخيار الأول لخدمات الونش في {displayName}؟</h2>
                <p>
                    عندما تبحث عن خدمة ونش موثوقة في {displayName}، فإن اختيار الشركة المناسبة أمر
                    في غاية الأهمية. نحن نقدم خدمات إنقاذ السيارات الأكثر احترافية في {displayName}
                    مع ضمان الوصول السريع والخدمة الممتازة. مع أكثر من 15 عاماً من الخبرة في مجال
                    إنقاذ ونقل السيارات، أصبحنا الخيار الأول لآلاف العملاء في {displayName} وما حولها.
                    تواصل معنا على {phoneNumbers[0]} أو {phoneNumbers[1]} أو {phoneNumbers[2]}.
                </p>

                {/* SEO inline image */}
                <div className="seo-inline-img">
                    <img src={article4} alt={`ونش إنقاذ سيارات في ${displayName}`} loading="lazy" width={600} height={400} />
                </div>

                <h3>خدمات الونش المتكاملة في {displayName}</h3>
                <p>
                    نحن نوفر مجموعة شاملة من خدمات الطوارئ للسيارات في {displayName}. سواء كنت بحاجة
                    إلى ونش لنقل سيارتك المعطلة، أو وصلة بطارية، أو تغيير إطار، أو توصيل وقود، فريقنا
                    في {displayName} جاهز دائماً لمساعدتك. نقدم خدمة إنقاذ السيارات على الطرق السريعة
                    والشوارع الداخلية والمناطق السكنية في {displayName}، مع ضمان الوصول إليك في أسرع
                    وقت ممكن بغض النظر عن موقعك داخل المنطقة.
                </p>

                <h3>أنواع السيارات التي نخدمها في {displayName}</h3>
                <p>
                    نمتلك أسطول متنوع من الونشات في {displayName} لخدمة جميع أنواع المركبات. نقوم بنقل
                    السيارات الصغيرة (الملاكي) مثل تويوتا كورولا، نيسان صني، كيا سيراتو، هيونداي إلنترا،
                    والسيارات الأوروبية مثل مرسيدس، بي ام دبليو، أودي. كما نخدم السيارات الدفع الرباعي
                    (SUV) مثل تويوتا فورتشنر، نيسان باترول، جيب رانجلر، ميتسوبيشي باجيرو. بالإضافة إلى
                    ذلك، لدينا ونشات مخصصة لنقل السيارات النقل الخفيف، الميني باصات، الكرفانات، الدراجات
                    النارية، والمعدات الخفيفة في {displayName}.
                </p>

                {/* SEO inline image 2 */}
                <div className="seo-inline-img">
                    <img src={article6} alt={`سحب ونقل السيارات في ${displayName}`} loading="lazy" width={600} height={400} />
                </div>

                <h3>حالات الطوارئ الشائعة التي نتعامل معها في {displayName}</h3>
                <p>
                    <strong>تعطل المحرك:</strong> إذا توقف محرك سيارتك فجأة في {displayName}، سواء كان
                    ذلك بسبب ارتفاع درجة الحرارة، أو مشكلة في الوقود، أو عطل ميكانيكي، فإن فريقنا يصل
                    إليك فوراً لنقل سيارتك إلى أقرب ورشة موثوقة في {displayName}.
                </p>
                <p>
                    <strong>نفاد البطارية:</strong> البطارية الفارغة من أكثر المشاكل شيوعاً في {displayName}،
                    خاصة في الصباح الباكر. نوفر خدمة شحن البطارية السريعة في موقعك، أو نقوم بتوصيل
                    بطارية جديدة وتركيبها لك في {displayName}.
                </p>
                <p>
                    <strong>ثقب الإطار:</strong> إذا تعرض إطار سيارتك للثقب في أحد شوارع {displayName}،
                    نقوم بتغيير الإطار في الموقع إذا كان لديك إطار احتياطي جيد، أو ننقل سيارتك إلى أقرب
                    مركز خدمة الإطارات في {displayName}.
                </p>
                <p>
                    <strong>نفاد الوقود:</strong> نسيت تعبئة الوقود ووقفت سيارتك في {displayName}؟ نحن
                    نقدم خدمة توصيل الوقود السريعة إلى موقعك، حتى تتمكن من متابعة طريقك دون الحاجة إلى
                    نقل السيارة.
                </p>
                <p>
                    <strong>حوادث الطرق:</strong> في حالة تعرض سيارتك لحادث في {displayName}، نوفر خدمة
                    إنقاذ متخصصة للتعامل مع السيارات المتضررة، مع مراعاة جميع إجراءات السلامة ونقل السيارة
                    بعناية فائقة إلى الورشة أو موقع التأمين.
                </p>
                <p>
                    <strong>السيارات العالقة:</strong> إذا علقت سيارتك في الرمال، الطين، أو أي مكان وعر
                    في {displayName} أو ضواحيها، لدينا المعدات المتخصصة والخبرة لانتشال سيارتك بأمان.
                </p>

                <h3>خبرة محلية عميقة في {displayName}</h3>
                <p>
                    فريقنا في {displayName} يمتلك معرفة عميقة بجميع الشوارع والطرق في المنطقة. نحن نعرف
                    كل زاوية في {displayName}، من الشوارع الرئيسية إلى الأزقة الضيقة والمناطق السكنية.
                    هذه المعرفة المحلية تمكننا من الوصول إليك بأسرع طريق ممكن، حتى في أوقات الذروة المرورية.
                    سائقونا يتابعون حركة المرور لحظياً ويستخدمون أفضل المسارات البديلة لتجنب الازدحام
                    والوصول إليك في {displayName} خلال 15-20 دقيقة فقط.
                </p>

                {/* SEO inline image 3 */}
                <div className="seo-inline-img">
                    <img src={article5} alt={`فريق إنقاذ محترف في ${displayName}`} loading="lazy" width={600} height={400} />
                </div>

                <h3>معدات حديثة وآمنة لحماية سيارتك في {displayName}</h3>
                <p>
                    نستخدم أحدث الونشات الهيدروليكية الحديثة في {displayName}، المجهزة بأنظمة تثبيت
                    متطورة تضمن عدم تعرض سيارتك لأي خدش أو ضرر أثناء عملية الرفع والنقل. جميع ونشاتنا
                    خضعت للصيانة الدورية وتخضع لفحوصات أمان صارمة. نستخدم أحزمة تثبيت قوية ومعتمدة،
                    وسادات حماية للإطارات والهيكل، وأنظمة رفع هيدروليكية سلسة تحافظ على سيارتك في وضع
                    مثالي طوال فترة النقل داخل {displayName} أو خارجها.
                </p>

                <h3>فريق محترف ومدرب في {displayName}</h3>
                <p>
                    جميع سائقي الونش في {displayName} حاصلون على تدريب احترافي معتمد في مجال إنقاذ
                    ونقل السيارات. بالإضافة إلى مهارات القيادة الماهرة، يمتلك فريقنا معرفة ميكانيكية
                    أساسية تمكنهم من تشخيص المشاكل البسيطة وتقديم المشورة الفنية. نحن نختار فريقنا بعناية
                    ونوفر لهم التدريب المستمر لضمان تقديم أفضل خدمة للعملاء في {displayName}. كما أن
                    جميع أفراد الفريق يتمتعون بأخلاقيات عمل عالية، احترافية في التعامل، والتزام تام
                    بالمواعيد.
                </p>

                <h3>أسعار تنافسية وشفافة في {displayName}</h3>
                <p>
                    نحن نؤمن بالشفافية الكاملة في التعاملات المالية. عندما تتصل بنا من {displayName}،
                    نقدم لك عرض سعر واضح ومحدد قبل تحرك الونش، بناءً على موقعك ونوع السيارة والوجهة المطلوبة.
                    لا توجد رسوم خفية أو مفاجآت. أسعارنا تنافسية للغاية مقارنة بجودة الخدمة التي نقدمها
                    في {displayName}. نقدم أيضاً خصومات خاصة للعملاء الدائمين والشركات التي تتعاقد معنا
                    في {displayName}. في حالات الطوارئ الليلية أو العطلات الرسمية، قد تنطبق رسوم إضافية
                    بسيطة، لكننا نبلغك بها مسبقاً بكل شفافية.
                </p>

                <h3>خدمة 24 ساعة طوال أيام الأسبوع في {displayName}</h3>
                <p>
                    الأعطال لا تحدث بموعد، لذلك نحن متواجدون دائماً لخدمتك في {displayName}. خدماتنا
                    متاحة 24 ساعة يومياً، 7 أيام في الأسبوع، بما في ذلك العطلات الرسمية والأعياد. سواء
                    كان ذلك في منتصف الليل، الفجر، أو أي وقت آخر، ستجد فريقنا جاهز للاستجابة لطلبك في
                    {displayName}. نحتفظ بعدة ونشات قيد الاستعداد في مواقع استراتيجية في {displayName}
                    لضمان أسرع وقت استجابة ممكن في جميع الأوقات.
                </p>

                <h3>تأمين شامل على جميع السيارات المنقولة من {displayName}</h3>
                <p>
                    راحة بالك هي أولويتنا. جميع السيارات التي ننقلها من {displayName} مؤمنة بالكامل
                    ضد أي أضرار قد تحدث أثناء عملية النقل (رغم أن هذا نادر جداً بفضل خبرتنا ومعداتنا
                    الحديثة). نعمل مع شركات تأمين معتمدة لضمان حماية كاملة لسيارتك من لحظة رفعها في
                    {displayName} حتى إيصالها بأمان إلى الوجهة المطلوبة.
                </p>

                <h3>خدمة النقل بين المحافظات من {displayName}</h3>
                <p>
                    إذا كنت بحاجة لنقل سيارتك من {displayName} إلى أي محافظة أخرى في مصر، نحن نقدم
                    خدمة النقل لمسافات طويلة بأسعار تنافسية. نقوم بنقل السيارات من {displayName} إلى
                    الإسكندرية، الساحل الشمالي، العين السخنة، شرم الشيخ، الغردقة، الأقصر، أسوان، وجميع
                    المحافظات المصرية. جميع عمليات النقل للمسافات الطويلة تتم بونشات مخصصة ومجهزة للرحلات
                    الطويلة، مع سائقين ذوي خبرة في السفر بالسيارات المحملة.
                </p>

                <h3>نقل السيارات الفارهة والكلاسيكية في {displayName}</h3>
                <p>
                    نحن ندرك أن بعض السيارات تحتاج إلى عناية خاصة. إذا كنت تمتلك سيارة فارهة، كلاسيكية،
                    رياضية، أو نادرة في {displayName}، فإننا نقدم خدمة نقل متخصصة بمعدات خاصة وعناية
                    فائقة. نستخدم ونشات مسطحة (Flatbed) لنقل السيارات الفارهة دون أي ضغط على نظام التعليق
                    أو الإطارات، ونوفر أغطية واقية لحماية الطلاء أثناء النقل في {displayName}.
                </p>

                <h3>خدمات إضافية نقدمها في {displayName}</h3>
                <p>
                    بالإضافة إلى خدمة الونش الأساسية، نقدم في {displayName} خدمات إضافية تشمل: فتح
                    السيارات المقفلة (في حالة نسيان المفاتيح داخل السيارة)، استخراج المفاتيح المكسورة
                    من القفل، توصيل قطع غيار طارئة، خدمة الميكانيكي المتنقل للإصلاحات البسيطة، فحص
                    السيارة قبل الشراء بالمعاينة الميكانيكية، وخدمة نقل السيارات الجديدة من المعارض
                    في {displayName}.
                </p>

                <h3>كيف نضمن سرعة الوصول إليك في {displayName}؟</h3>
                <p>
                    نعتمد على استراتيجية التموضع الذكي في {displayName}. لدينا عدة نقاط انتظار استراتيجية
                    موزعة في {displayName} وحولها، بحيث يكون دائماً هناك ونش قريب منك. نستخدم نظام GPS
                    متطور لتتبع جميع ونشاتنا في الوقت الفعلي واختيار أقرب ونش متاح لموقعك في {displayName}.
                    كما نتعاون مع تطبيقات خرائط المرور لمعرفة أفضل المسارات وتجنب الازدحامات، مما يضمن
                    وصولنا إليك في {displayName} في أسرع وقت ممكن - عادة خلال 15-20 دقيقة.
                </p>

                <h3>لماذا العملاء في {displayName} يثقون بنا؟</h3>
                <ul className="seo-list">
                    <li><strong>السرعة الفائقة:</strong> نصل خلال 15-20 دقيقة فقط في {displayName}</li>
                    <li><strong>الأمان المطلق:</strong> معدات حديثة ومعتمدة لحماية سيارتك بالكامل</li>
                    <li><strong>الاحترافية العالية:</strong> فريق مدرب ومعتمد على أعلى مستوى</li>
                    <li><strong>الشفافية التامة:</strong> أسعار واضحة بدون أي رسوم خفية أو مفاجآت</li>
                    <li><strong>التوفر الدائم:</strong> خدمة 24/7 طوال أيام الأسبوع في {displayName}</li>
                    <li><strong>التأمين الشامل:</strong> جميع السيارات المنقولة مؤمنة بالكامل</li>
                    <li><strong>الخبرة الطويلة:</strong> أكثر من 15 عاماً في خدمة عملاء {displayName}</li>
                    <li><strong>المعرفة المحلية:</strong> معرفة تامة بجميع شوارع وطرق {displayName}</li>
                    <li><strong>خدمة العملاء:</strong> فريق دعم عملاء محترف ومتجاوب</li>
                    <li><strong>التقييمات الممتازة:</strong> أكثر من 1,250 تقييم إيجابي من عملاء {displayName}</li>
                </ul>

                <h3>طرق التواصل معنا في {displayName}</h3>
                <p>
                    يمكنك التواصل معنا في {displayName} بعدة طرق سهلة: الاتصال الهاتفي المباشر على
                    أرقامنا المتاحة 24/7 وهي {phoneNumbers[0]} و{phoneNumbers[1]} و{phoneNumbers[2]}،
                    إرسال رسالة واتساب (نستجيب فوراً)، أو من خلال موقعنا الإلكتروني.
                    عند الاتصال، سيطلب منك موظف خدمة العملاء بعض المعلومات البسيطة: موقعك الحالي في
                    {displayName} (يمكنك إرسال موقع GPS)، نوع المشكلة، نوع وموديل السيارة، والوجهة المطلوبة.
                    بعد ذلك، سنعطيك عرض سعر فوري وزمن الوصول المتوقع، وسيتحرك الونش إليك مباشرة في {displayName}.
                </p>

                <h3>نصائح للحفاظ على سيارتك وتجنب الأعطال في {displayName}</h3>
                <p>
                    للتقليل من احتمالية احتياجك لخدمة الونش في {displayName}، ننصحك بإجراء صيانة دورية
                    لسيارتك، فحص البطارية بانتظام خاصة قبل الصيف والشتاء، التأكد من مستوى الزيت والسوائل،
                    فحص ضغط الإطارات وحالتها، عدم تجاهل أي أصوات أو إنذارات غير طبيعية، والاحتفاظ برقمنا {phoneNumbers[0]} محفوظاً للطوارئ.
                    مع ذلك، إذا حدث أي طارئ في {displayName}، نحن هنا دائماً لمساعدتك.
                </p>

                <h3>تغطية شاملة في {displayName} وما حولها</h3>
                <p>
                    بالإضافة إلى {displayName}، نحن نخدم أيضاً جميع المناطق المحيطة في {governorateName}.
                    أينما كنت في المنطقة، يمكنك الاعتماد علينا للحصول على خدمة إنقاذ سريعة وموثوقة.
                    شبكة تغطيتنا واسعة ومتطورة، مما يضمن لك خدمة متميزة سواء كنت في قلب {displayName}
                    أو في المناطق المحيطة بها.
                </p>

                <h3>التزامنا بجودة الخدمة في {displayName}</h3>
                <p>
                    نحن لا نقدم مجرد خدمة نقل سيارات في {displayName}، بل نقدم تجربة إنقاذ متكاملة تراعي
                    راحتك وسلامتك. نلتزم بالوصول في الوقت المحدد، التعامل باحترافية مطلقة، الحفاظ على
                    سيارتك كأنها ملكنا، وتقديم الدعم والمشورة الفنية. رضا العملاء هو مقياس نجاحنا، ولذلك
                    نسعى دائماً لتحسين خدماتنا وتلبية توقعات عملائنا في {displayName} وتجاوزها.
                </p>

                <h3>اتصل بنا الآن للحصول على خدمة ونش في {displayName}</h3>
                <p>
                    لا تتردد في الاتصال بنا في أي وقت من اليوم أو الليل. خدمة الطوارئ الخاصة بنا
                    في {displayName} متاحة على مدار الساعة، وفريقنا جاهز دائماً للاستجابة لطلبك
                    بأسرع وقت ممكن. نحن على بعد مكالمة واحدة فقط، ومستعدون لمساعدتك في أي حالة طارئة
                    في {displayName}. اتصل الآن على {phoneNumbers[0]} أو {phoneNumbers[1]} أو {phoneNumbers[2]} أو راسلنا عبر
                    واتساب للحصول على مساعدة فورية!
                </p>
            </div>
        </div>
    </section>

    {/* Extra SEO Content — Area-Focused Keywords */ }
    <section className="seo-content seo-extra">
        <div className="seo-container">
            <div className="seo-text">
                <h2>ونش انقاذ {displayName} | خصم 50% | {phoneNumbers[0]}</h2>
                <p>
                    خدمة <strong>ونش انقاذ {displayName}</strong> هي الأسرع والأكثر موثوقية في المنطقة. نحن نوفر لك <strong>خصم 50%</strong> على جميع
                    خدمات الإنقاذ وسحب السيارات في {displayName} والمناطق المحيطة. سواء احتجت لونش لسحب سيارتك المعطلة، أو خدمة شحن بطارية، أو تغيير إطار،
                    أو توصيل وقود — فريقنا جاهز لك 24 ساعة. فقط اتصل على <strong>{phoneNumbers[0]}</strong> وسيصلك أقرب ونش انقاذ في {displayName} خلال 20 دقيقة.
                </p>

                <h3>رقم ونش انقاذ {displayName} — متاح 24 ساعة</h3>
                <p>
                    احفظ رقم <strong>ونش انقاذ {displayName}</strong> الآن لاستخدامه في حالات الطوارئ: <strong>{phoneNumbers[0]}</strong> أو <strong>{phoneNumbers[1]}</strong> أو <strong>{phoneNumbers[2]}</strong>.
                    فريقنا يرد على مدار الساعة طوال أيام الأسبوع بما فيها الأعياد والعطلات الرسمية. لا تضيع وقتك في البحث عن <strong>اقرب ونش انقاذ في {displayName}</strong> —
                    نحن دائماً في انتظار مكالمتك!
                </p>

                <h3>تليفون ونش انقاذ {displayName} — اتصل الآن واستفد بخصم 50%</h3>
                <p>
                    هل تبحث عن <strong>تليفون ونش انقاذ {displayName}</strong>؟ الرقم هو <strong>{phoneNumbers[0]}</strong>. عند اتصالك بنا ستجد فريقاً محترفاً
                    جاهزاً لمساعدتك فوراً، وستحصل تلقائياً على <strong>خصم 50%</strong> على قيمة الخدمة. نضمن لك أسعاراً شفافة ومحددة قبل تحرك الونش، بدون
                    أي مفاجآت أو رسوم خفية. خدمة ونش {displayName} الأولى في المنطقة بأمان تام ووقت وصول لا يتجاوز 20 دقيقة.
                </p>

                <h3>أسرع ونش انقاذ سيارات في {displayName}</h3>
                <p>
                    ما الذي يجعلنا <strong>أسرع ونش انقاذ سيارات في {displayName}</strong>؟ لدينا ونشات متمركزة في نقاط استراتيجية داخل {displayName}،
                    وأسطول متنوع من الونشات الهيدروليكية والمسطحة لخدمة جميع أنواع السيارات. نستخدم نظام GPS لتتبع أقرب ونش لموقعك في {displayName}،
                    ونحن نتابع حركة المرور لاختيار أسرع مسار للوصول إليك. الهدف دائماً هو الوصول إليك في <strong>أقل من 20 دقيقة</strong> في {displayName}.
                </p>

                <h3>سحب سيارة {displayName} — جميع الأنواع والموديلات</h3>
                <p>
                    خدمة <strong>سحب سيارة {displayName}</strong> لدينا تشمل جميع أنواع المركبات: السيارات الملاكي الصغيرة والكبيرة، سيارات الدفع الرباعي (SUV)،
                    الميني باصات، الشاحنات الخفيفة، الكرفانات، الدراجات النارية، والسيارات الفارهة والكلاسيكية. لدينا الونش المناسب لكل نوع من السيارات في {displayName}.
                    اتصل بنا على <strong>{phoneNumbers[0]}</strong> وأخبرنا بنوع سيارتك وموقعك في {displayName} وسنرسل لك الونش المناسب فوراً مع <strong>خصم 50%</strong>.
                </p>

                <h3>خدمة ونش انقاذ {displayName} للطرق السريعة والشوارع الداخلية</h3>
                <p>
                    سواء تعطلت سيارتك على الطريق الدائري، أو الطريق السريع، أو في الشوارع الداخلية والأحياء السكنية في {displayName}، فريقنا يصل إليك في أي مكان.
                    نتعامل مع جميع حالات الطوارئ على الطرق في {displayName}: تعطل المحرك، ثقب الإطار، نفاد البطارية، نفاد الوقود، حوادث الطرق، والسيارات العالقة.
                    كل ما تحتاجه هو الاتصال على <strong>{phoneNumbers[0]}</strong> والحصول على المساعدة الفورية مع <strong>خصم 50%</strong> في {displayName}.
                </p>
            </div>
        </div>
    </section>

    {/* Emergency Contact */ }
    <section className="area-emergency">
        <div className="emergency-content">
            <h2 className="emergency-title">
                تحتاج ونش في {displayName} الآن؟
            </h2>
            <p className="emergency-description">
                اتصل بنا فوراً وسيصل فريقنا إليك في {displayName} خلال دقائق!
            </p>
            <div className="emergency-buttons">
                {phoneNumbers.map((num, i) => (
                    <a key={i} href={`tel:+2${num}`} className={`emergency-button ${i === 0 ? 'primary' : i === 1 ? 'secondary' : 'tertiary'}`}>
                        <Phone color={i === 0 ? 'black' : 'var(--accent)'} style={{ marginBottom: '-6px' }} />
                        {num}
                    </a>
                ))}
                <a
                    href={`https://wa.me/+201553877630?text=${encodeURIComponent(`أحتاج خدمة ونش في ${displayName}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="emergency-button whatsapp"
                >
                    <MessageCircle color='white' style={{ marginBottom: '-6px' }} /> واتساب
                </a>
            </div>
        </div>
    </section>
        </div >
    );
};

export default AreaDetails;
